import { useEffect, useState } from 'react'
import { onToast } from '../lib/toast.js'
import { CloseIcon } from './icons.jsx'

// Bottom-right toast stack (mounted once in the Shell). Fixed dark chrome in
// both themes, like tooltips — quiet, never blocks the UI.
const TONE_ICON = {
  // A real spinner ring — a tumbling sparkles glyph read as a glitch, not
  // progress (same ring the tables use while loading).
  progress: (
    <span
      className="block h-4 w-4 animate-spin rounded-full [animation-duration:0.8s] [border:2px_solid_rgba(255,255,255,0.18)] [border-top-color:var(--primary)]"
      role="status"
      aria-label="Working"
    />
  ),
  success: (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-[#4ade80]" aria-hidden="true">
      {/* coolicons: line/Check.svg */}
      <path d="M6 12L10.2426 16.2426L18.727 7.75732" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Was a bare "⚠" text character, which is a font glyph rather than an icon:
  // it renders differently per platform and could not take the 24-grid line
  // weight the rest of the set uses. Same tones as before.
  warn: (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-[#fbbf24]" aria-hidden="true">
      {/* coolicons: line/Triangle_Warning.svg */}
      <path d="M12 9.00006V13.0001M4.37891 15.1999C3.46947 16.775 3.01489 17.5629 3.08281 18.2092C3.14206 18.7729 3.43792 19.2851 3.89648 19.6182C4.42204 20.0001 5.3309 20.0001 7.14853 20.0001H16.8515C18.6691 20.0001 19.5778 20.0001 20.1034 19.6182C20.5619 19.2851 20.8579 18.7729 20.9172 18.2092C20.9851 17.5629 20.5307 16.775 19.6212 15.1999L14.7715 6.79986C13.8621 5.22468 13.4071 4.43722 12.8135 4.17291C12.2957 3.94236 11.704 3.94236 11.1862 4.17291C10.5928 4.43711 10.1381 5.22458 9.22946 6.79845L4.37891 15.1999ZM12.0508 16.0001V16.1001L11.9502 16.1003V16.0001H12.0508Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-[#fca5a5]" aria-hidden="true">
      {/* coolicons: line/Triangle_Warning.svg */}
      <path d="M12 9.00006V13.0001M4.37891 15.1999C3.46947 16.775 3.01489 17.5629 3.08281 18.2092C3.14206 18.7729 3.43792 19.2851 3.89648 19.6182C4.42204 20.0001 5.3309 20.0001 7.14853 20.0001H16.8515C18.6691 20.0001 19.5778 20.0001 20.1034 19.6182C20.5619 19.2851 20.8579 18.7729 20.9172 18.2092C20.9851 17.5629 20.5307 16.775 19.6212 15.1999L14.7715 6.79986C13.8621 5.22468 13.4071 4.43722 12.8135 4.17291C12.2957 3.94236 11.704 3.94236 11.1862 4.17291C10.5928 4.43711 10.1381 5.22458 9.22946 6.79845L4.37891 15.1999ZM12.0508 16.0001V16.1001L11.9502 16.1003V16.0001H12.0508Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
}

export default function ToastHost() {
  const [toasts, setToasts] = useState([])

  useEffect(
    () =>
      onToast((t) => {
        setToasts((list) => [...list, t])
        setTimeout(() => setToasts((list) => list.filter((x) => x.id !== t.id)), t.duration)
      }),
    [],
  )

  if (!toasts.length) return null
  return (
    <div className="pointer-events-none fixed right-5 z-[130] flex w-[min(360px,calc(100vw-40px))] flex-col gap-y-2 transition-[bottom] duration-200 [bottom:calc(20px+var(--compose-dock-h,0px))]">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto flex items-start gap-x-3 rounded-[12px] border border-[#333333] bg-[#151515] px-4 py-3.5 shadow-[0_12px_32px_rgba(0,0,0,0.45)] animate-[toastIn_0.22s_ease]"
        >
          <span className="mt-[2px] flex-none">{TONE_ICON[t.tone] || TONE_ICON.success}</span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-semibold leading-[1.4] text-[#f5f5f5]">{t.title}</div>
            {t.body && <div className="mt-0.5 text-[12.5px] leading-[1.5] text-[rgba(255,255,255,0.65)]">{t.body}</div>}
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}
            className="flex-none border-none bg-transparent p-0 text-[rgba(255,255,255,0.4)] transition-colors hover:text-white [&_svg]:h-[14px] [&_svg]:w-[14px]"
          >
            <CloseIcon />
          </button>
        </div>
      ))}
    </div>
  )
}
